import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Package } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    specifications: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleInquiryClick = (product) => {
    setSelectedProduct(product);
    setInquiryModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/product-inquiries`, {
        ...formData,
        product_type: selectedProduct.name,
      });
      toast.success('Inquiry submitted successfully!');
      setInquiryModalOpen(false);
      setFormData({ name: '', company: '', email: '', specifications: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const roundBars = products.filter((p) => p.category === 'Round Bars');
  const pipes = products.filter((p) => p.category === 'Pipes & Tubes');
  const plates = products.filter((p) => p.category === 'Plates');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="products-hero-section"
        className="relative py-32 lg:py-40 overflow-hidden bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-6">
            Product Range
          </div>
          <h1
            data-testid="products-headline"
            className="text-5xl lg:text-7xl font-heading font-light tracking-tighter text-white mb-6 leading-[1.1] max-w-4xl"
          >
            Premium Metal Stock & Custom Components
          </h1>
          <p className="text-base lg:text-lg text-omni-text-secondary leading-relaxed max-w-3xl">
            Explore our comprehensive range of aerospace and automotive-grade Aluminium and
            Stainless Steel materials, available in various grades, sizes, and specifications.
          </p>
        </div>
      </section>

      {/* Round Bars Section */}
      <section data-testid="round-bars-section" className="py-24 lg:py-32 bg-omni-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-omni-accent" />
              </div>
              <h2 className="text-4xl font-heading font-normal tracking-tight text-white">
                Round Bars (Solid)
              </h2>
            </div>
            <p className="text-base text-omni-text-secondary leading-relaxed max-w-3xl">
              High-precision solid round bars engineered for demanding applications requiring
              exceptional dimensional accuracy and material consistency.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-omni-text-secondary">Loading products...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {roundBars.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                >
                  {/* Product Image */}
                  <div className="aspect-video mb-6 overflow-hidden border border-white/10">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h3 className="text-2xl font-heading font-medium text-white mb-3">
                    {product.name}
                  </h3>
                  <p className="text-sm text-omni-text-secondary leading-relaxed mb-6">
                    {product.description}
                  </p>

                  {/* Specifications Table */}
                  <div className="mb-6">
                    <table className="w-full text-left border-collapse">
                      <tbody>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Material
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.material}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Grades
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.grades.join(', ')}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Sizes
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.sizes}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Tolerances
                          </td>
                          <td className="py-3 px-4 text-white">{product.tolerances}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <button
                    data-testid={`inquire-button-${product.id}`}
                    onClick={() => handleInquiryClick(product)}
                    className="w-full bg-transparent border border-omni-accent text-omni-accent px-6 py-3 hover:bg-omni-accent/10 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    Inquire About This Specification
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pipes & Tubes Section */}
      <section
        data-testid="pipes-section"
        className="py-24 lg:py-32 bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-omni-accent" />
              </div>
              <h2 className="text-4xl font-heading font-normal tracking-tight text-white">
                Pipes & Tubes (Hollow)
              </h2>
            </div>
            <p className="text-base text-omni-text-secondary leading-relaxed max-w-3xl">
              Seamless and welded pipes engineered for high-pressure applications and corrosive
              environments in aerospace and automotive systems.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-omni-text-secondary">Loading products...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {pipes.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="bg-omni-background border border-white/10 p-8 hover:border-omni-accent/50 transition-colors shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                >
                  {/* Product Image */}
                  <div className="aspect-video mb-6 overflow-hidden border border-white/10">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h3 className="text-2xl font-heading font-medium text-white mb-3">
                    {product.name}
                  </h3>
                  <p className="text-sm text-omni-text-secondary leading-relaxed mb-6">
                    {product.description}
                  </p>

                  {/* Specifications Table */}
                  <div className="mb-6">
                    <table className="w-full text-left border-collapse">
                      <tbody>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Material
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.material}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Grades
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.grades.join(', ')}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Sizes
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.sizes}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Tolerances
                          </td>
                          <td className="py-3 px-4 text-white">{product.tolerances}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <button
                    data-testid={`inquire-button-${product.id}`}
                    onClick={() => handleInquiryClick(product)}
                    className="w-full bg-transparent border border-omni-accent text-omni-accent px-6 py-3 hover:bg-omni-accent/10 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    Inquire About This Specification
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Plates (Die & Mould) Section */}
      <section
        data-testid="plates-section"
        className="py-24 lg:py-32 bg-omni-background"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-omni-accent" />
              </div>
              <h2 className="text-4xl font-heading font-normal tracking-tight text-white">
                Aluminium Plates (Die & Mould)
              </h2>
            </div>
            <p className="text-base text-omni-text-secondary leading-relaxed max-w-3xl">
              Premium stress-relieved aluminium plates engineered for tooling, injection moulds,
              and precision die manufacturing — offering exceptional machinability and dimensional
              stability.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-omni-text-secondary">Loading products...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {plates.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="bg-omni-background-secondary border border-white/10 p-8 hover:border-omni-accent/50 transition-colors shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                >
                  {/* Product Image */}
                  <div className="aspect-video mb-6 overflow-hidden border border-white/10">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h3 className="text-2xl font-heading font-medium text-white mb-3">
                    {product.name}
                  </h3>
                  <p className="text-sm text-omni-text-secondary leading-relaxed mb-6">
                    {product.description}
                  </p>

                  {/* Specifications Table */}
                  <div className="mb-6">
                    <table className="w-full text-left border-collapse">
                      <tbody>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Material
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.material}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Grades
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.grades.join(', ')}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b border-white/10 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Sizes
                          </td>
                          <td className="py-3 px-4 border-b border-white/5 text-white">
                            {product.sizes}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-xs uppercase tracking-wider text-omni-text-secondary">
                            Tolerances
                          </td>
                          <td className="py-3 px-4 text-white">{product.tolerances}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <button
                    data-testid={`inquire-button-${product.id}`}
                    onClick={() => handleInquiryClick(product)}
                    className="w-full bg-transparent border border-omni-accent text-omni-accent px-6 py-3 hover:bg-omni-accent/10 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    Inquire About This Specification
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Inquiry Modal */}
      {inquiryModalOpen && (
        <div
          data-testid="inquiry-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setInquiryModalOpen(false)}
        >
          <div
            className="bg-omni-background-secondary border border-white/10 p-8 max-w-2xl w-full shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              data-testid="modal-headline"
              className="text-2xl font-heading font-medium text-white mb-6"
            >
              Product Inquiry: {selectedProduct?.name}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                  Your Name *
                </label>
                <input
                  data-testid="inquiry-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                  Company Name *
                </label>
                <input
                  data-testid="inquiry-company-input"
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                  Email Address *
                </label>
                <input
                  data-testid="inquiry-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                  Specifications & Requirements *
                </label>
                <textarea
                  data-testid="inquiry-specifications-input"
                  required
                  rows={4}
                  value={formData.specifications}
                  onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                  className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                  placeholder="Please provide details about your requirements..."
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="inquiry-submit-button"
                  disabled={submitting}
                  className="flex-1 bg-omni-accent text-white px-6 py-3 hover:bg-omni-accent-hover transition-all font-medium disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
                <button
                  type="button"
                  data-testid="inquiry-cancel-button"
                  onClick={() => setInquiryModalOpen(false)}
                  className="flex-1 bg-transparent border border-white/20 text-white px-6 py-3 hover:bg-white/5 transition-all font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Products;
